import { StorageSerializers, useStorage } from '@vueuse/core';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';

import { DashboardAPI } from '@/apis';
import { parseIndustryName } from '@/app/dashboards/utils/benchmarks.utils';
import {
  FILTER_ACTION_TYPES,
  trackFilterInteraction,
} from '@/components/dashingUI/constants/mixpanel';
import { INDUSTRY_FOLLOWER_BUCKETS } from '@/models/dashboards/benchmark-type.enum';
import { useIdentityStore } from '@/stores/identity';
import { logger } from '@/utils/logger';

export interface FollowerBucket {
  id: number;
  name: string;
}

export interface HubspotIndustry {
  id: number;
  name: string;
  follower_buckets: FollowerBucket[];
}

export interface BenchmarkSelection {
  industryId: number;
  industry: string;
  followerBucketId: number;
  followerBucket: string;
  bucketKey: string;
}

type BucketKey = keyof typeof INDUSTRY_FOLLOWER_BUCKETS;

interface MenuItem {
  label: string;
  command: () => void;
}

interface MenuGroup {
  label: string;
  items: MenuItem[];
}

export interface HubspotIndustryOption {
  value: string;
  text: string;
  label: string;
  id: number;
  follower_bucket: string;
  follower_bucket_id: number;
}

export const useDashboardIndustriesStore = defineStore('dashboard-industries', () => {
  const hubspotIndustries = ref<HubspotIndustry[]>([]);
  const { currentBrandLabel } = storeToRefs(useIdentityStore());

  // Stores benchmark selections per brand in localStorage as { [brandLabel]: BenchmarkSelection }
  const benchmarkSelections = useStorage<Record<string, BenchmarkSelection>>(
    'tsi-benchmark-selections',
    {},
    localStorage,
    { serializer: StorageSerializers.object },
  );

  const benchmarkSelection = computed<BenchmarkSelection | null>({
    get: () => {
      const brand = currentBrandLabel.value;
      if (!brand) return null;
      return benchmarkSelections.value[brand] ?? null;
    },
    set: (value) => {
      const brand = currentBrandLabel.value;
      if (!brand) return;
      if (value === null) {
        const copy = { ...benchmarkSelections.value };
        delete copy[brand];
        benchmarkSelections.value = copy;
      } else {
        benchmarkSelections.value = { ...benchmarkSelections.value, [brand]: value };
      }
    },
  });

  watch([hubspotIndustries, currentBrandLabel], () => {
    validateStoredSelection();
  });

  const benchmarkMenuModel = computed<MenuGroup[]>(() => {
    if (!hubspotIndustries.value?.length) return [];

    return hubspotIndustries.value.map((industry) => ({
      label: industry.name,
      items: (industry.follower_buckets ?? []).map((bucket) => {
        const bucketConfig = INDUSTRY_FOLLOWER_BUCKETS[bucket.name as BucketKey];
        const bucketLabel = bucketConfig?.text ?? bucket.name;
        const bucketKey = bucket.name;
        return {
          label: bucketLabel,
          command: () => {
            benchmarkSelection.value = {
              industryId: industry.id,
              industry: industry.name,
              followerBucketId: bucket.id,
              followerBucket: bucketLabel,
              bucketKey,
            };
            trackFilterInteraction({
              action: FILTER_ACTION_TYPES.APPLY,
              filterName: 'IndustryBenchmark',
              selectedOption: { industry: industry.name, followerBucket: bucketLabel },
            });
          },
        };
      }),
    }));
  });

  const benchmarkFilterLabel = computed(() => {
    if (!benchmarkSelection.value) return 'Choose Industry';
    return `${benchmarkSelection.value.industry}: ${benchmarkSelection.value.followerBucket}`;
  });

  const selectedBenchmarkIndustry = computed<HubspotIndustry | null>(() => {
    if (!benchmarkSelection.value) return null;
    return (
      hubspotIndustries.value.find((ind) => ind.id === benchmarkSelection.value!.industryId) ?? null
    );
  });

  const selectedBenchmarkBucket = computed<FollowerBucket | null>(
    () =>
      selectedBenchmarkIndustry.value?.follower_buckets?.find(
        (b) => b.id === benchmarkSelection.value?.followerBucketId,
      ) ?? null,
  );

  function clearBenchmarkSelection() {
    if (benchmarkSelection.value) {
      trackFilterInteraction({
        action: FILTER_ACTION_TYPES.REMOVE,
        filterName: 'IndustryBenchmark',
        selectedOption: {},
      });
    }
    benchmarkSelection.value = null;
  }

  // Flattens hubspot industries with follower buckets into dropdown options
  // Each option displays as "Industry: Bucket" (e.g., "Beauty: Overall", "Beauty: 10K - 200K")
  const hubspotIndustryOptions = computed<HubspotIndustryOption[]>(() => {
    const options: HubspotIndustryOption[] = [];

    hubspotIndustries.value.forEach((industry) => {
      const buckets = industry.follower_buckets ?? [];
      const hasGranularFollowerBuckets = !(
        buckets.length === 1 && buckets[0].name === INDUSTRY_FOLLOWER_BUCKETS.BUCKET_OVERALL.value
      );
      buckets.forEach((bucket) => {
        const bucketConfig = INDUSTRY_FOLLOWER_BUCKETS[bucket.name as BucketKey];
        const bucketLabel = bucketConfig?.text ?? bucket.name;
        const text = parseIndustryName(industry.name, bucketLabel, hasGranularFollowerBuckets);
        const value = parseIndustryName(
          String(industry.id),
          bucket.name,
          hasGranularFollowerBuckets,
        );

        options.push({
          value,
          text,
          label: text,
          id: industry.id,
          follower_bucket: bucket.name,
          follower_bucket_id: bucket.id,
        });
      });
    });

    return options;
  });

  function validateStoredSelection() {
    if (!benchmarkSelection.value || !hubspotIndustries.value.length) return;

    if (!selectedBenchmarkIndustry.value || !selectedBenchmarkBucket.value) {
      benchmarkSelection.value = null;
    }
  }

  async function fetchHubspotIndustries() {
    try {
      const response = await DashboardAPI.getHubspotIndustries();
      hubspotIndustries.value = response?.data ?? [];
    } catch (error) {
      logger.error('Failed to fetch hubspot industries:', error);
    }
  }

  return {
    hubspotIndustries,
    benchmarkSelection,
    benchmarkMenuModel,
    benchmarkFilterLabel,
    selectedBenchmarkIndustry,
    selectedBenchmarkBucket,
    hubspotIndustryOptions,
    clearBenchmarkSelection,
    fetchHubspotIndustries,
  };
});
